import React, {useEffect, useState} from 'react';
import ProCard from '@ant-design/pro-card';
import {wdiCore} from '@/components/WdiControls';
import {DatePicker} from 'antd';
import {FormSa} from '@/services/sales/sa';
import {Enum} from '@/enums/system';
import {GridApi} from "@/services/gridapi";
import {NsGrid} from "@/components/GridWidget/GridDefProps";
import {initFieldDisplayFormater} from "@/components/WdiBus/TableColumnRanderFormats";
import {DetailGrid} from "@/components/GridWidget";
import {ButtonColumn, FilterColumn, GridFilterForm} from "@/components/GridWidget/GridFilterForm";
import {TablePaginationConfig} from "antd/lib/table";
import {SorterResult} from "antd/lib/table/interface";
import {getSchemaColumns, toExcel} from "@/components/WdiControls/WdiExcel";
import {Position} from "@/services/basic/position";
import moment from "moment/moment";
import {WdiSelect, WdiSelectFormat} from "@/components/WdiControls/WdiSelect";
import {CustSysAdd} from "@/services/mdm/custsysadd";
import {Product} from "@/services/mdm/product";

type SalesAndCostProps = {
  formSa?: API.FormSa;
  formSaDetails?: API.FormSaDetail[];
  boLevelOpts?: any[];
}

type TableParams = {
  pagination?: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any
};

export const getSalesAndCostSoldToOptions = async () => {
  const respArr = await Promise.all([
    Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.VIEW_SOLD_LEVEL1]}),
    Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.VIEW_SOLD_LEVEL2]}),
    Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.VIEW_SOLD_LEVEL3]}),
  ]);
  const boLevel1Access = respArr[0].data;
  const boLevel2Access = respArr[1].data;
  const boLevel3Access = respArr[2].data;
  const boLevelOptions: any[] = [];
  if (boLevel1Access) {
    boLevelOptions.push({value: 1, label: "第1层(大区)"});
  }
  if (boLevel2Access) {
    boLevelOptions.push({value: 2, label: "第2层(省办)"});
  }
  if (boLevel3Access) {
    boLevelOptions.push({value: 3, label: "第3层(市办)"});
  }
  return boLevelOptions;
}

export const getSalesAndCostRegionOptions = async () => {
  const respArr = await Promise.all([
    Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.VIEW_REGION_LEVEL1]}),
    Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.VIEW_REGION_LEVEL2]}),
    Position.access({useCaseId: Enum.UseCase.SA, operateTypeIds: [Enum.OperateType.VIEW_REGION_LEVEL3]}),
  ]);
  const boLevel1Access = respArr[0].data;
  const boLevel2Access = respArr[1].data;
  const boLevel3Access = respArr[2].data;
  const boLevelOptions: any[] = [];
  if (boLevel1Access) {
    boLevelOptions.push({value: 1, label: "第1层(大区)"});
  }
  if (boLevel2Access) {
    boLevelOptions.push({value: 2, label: "第2层(省办)"});
  }
  if (boLevel3Access) {
    boLevelOptions.push({value: 3, label: "第3层(市办)"});
  }
  return boLevelOptions;
}

const initRowFieldConfigs = () => {
  return [
    "custSysAddCode", "boId", "brandId", "amountSa", "grossRate1", "opProfitRate1", "ytdRoi1", "revenueTarget1",
    "revenueActual1", "revenueTargetRate1", "contractRoi1", "soldToPolicyRoi1", "promotionPolicyRoi1",
    "regionProjectRoi1", "groupProjectRoi1", "priceRoi1", "grossRate2", "opProfitRate2", "grossRate3", "opProfitRate3",
    "grossRate4", "opProfitRate4", "ytdRoi2", "revenueTarget2", "revenueActual2", "revenueTargetRate2",
    "contractRoi2", "soldToPolicyRoi2", "promotionPolicyRoi2", "regionProjectRoi2", "groupProjectRoi2", "priceRoi2",
    "revenueSum2", "revenueCost", "revenueCostExDepre", "amountContract", "amountPromotionPolicy",
    "amountSoldToPolicy", "amountAreaProject", "amountGroupProject", "amountPrice"
  ];
};

const formatFormSchema = (settings: any) => {
  settings.formSchema.allFields.boId.width = 200;
  settings.formSchema.allFields.brandId.width = 200;
  if (settings.formSchema.allFields.amountSa) {
    settings.formSchema.allFields.amountSa.width = 120;
  }
  settings.formSchema.allFields.grossRate1.width = 200;
  settings.formSchema.allFields.opProfitRate1.width = 215;
  settings.formSchema.allFields.ytdRoi1.width = 150;
  settings.formSchema.allFields.revenueTarget1.width = 160;
  settings.formSchema.allFields.revenueActual1.width = 160;
  settings.formSchema.allFields.revenueTargetRate1.width = 150;
  settings.formSchema.allFields.contractRoi1.width = 150;
  settings.formSchema.allFields.soldToPolicyRoi1.width = 180;
  settings.formSchema.allFields.promotionPolicyRoi1.width = 180;
  settings.formSchema.allFields.regionProjectRoi1.width = 180;
  settings.formSchema.allFields.groupProjectRoi1.width = 180;
  settings.formSchema.allFields.priceRoi1.width = 150;
  settings.formSchema.allFields.grossRate2.width = 190;
  settings.formSchema.allFields.opProfitRate2.width = 200;
  settings.formSchema.allFields.grossRate3.width = 200;
  settings.formSchema.allFields.opProfitRate3.width = 215;
  settings.formSchema.allFields.grossRate4.width = 200;
  settings.formSchema.allFields.opProfitRate4.width = 215;
  settings.formSchema.allFields.ytdRoi2.width = 150;
  settings.formSchema.allFields.revenueTarget2.width = 180;
  settings.formSchema.allFields.revenueActual2.width = 180;
  settings.formSchema.allFields.revenueTargetRate2.width = 150;
  settings.formSchema.allFields.contractRoi2.width = 150;
  settings.formSchema.allFields.soldToPolicyRoi2.width = 180;
  settings.formSchema.allFields.promotionPolicyRoi2.width = 180;
  settings.formSchema.allFields.regionProjectRoi2.width = 180;
  settings.formSchema.allFields.groupProjectRoi2.width = 180;
  settings.formSchema.allFields.priceRoi2.width = 150;
  settings.formSchema.allFields.revenueSum1.width = 170;
  settings.formSchema.allFields.revenueSum2.width = 170;
  settings.formSchema.allFields.revenueCost.width = 190;
  settings.formSchema.allFields.revenueCostExDepre.width = 210;
  settings.formSchema.allFields.amountContract.width = 100;
  settings.formSchema.allFields.amountPromotionPolicy.width = 120;
  settings.formSchema.allFields.amountSoldToPolicy.width = 120;
  settings.formSchema.allFields.amountRegionProject.width = 120;
  settings.formSchema.allFields.amountGroupProject.width = 120;
  settings.formSchema.allFields.amountPrice.width = 100;
};

/**
 * 销量与费用(客户)
 */
export const SalesAndCostSoldTo: React.FC<SalesAndCostProps> = (props) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    filterOptions: null,
  });
  const [filterDefValues, setFilterDefValues] = useState<any>({});

  const {formSa, boLevelOpts} = props;

  useEffect(() => {
    GridApi.searchConfig({id: 'SalesAndCostSoldTo', allowModifyColumns: true}).then((response) => {
      const settings: any = Object.assign({}, response.data, {
        actionsAlign: NsGrid.ActionsAlign.Left,
        // showAggr: true,
        showExpandedRow: true,
      });

      initFieldDisplayFormater(settings.formSchema.allFields);
      formatFormSchema(settings);

      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, []);

  const pageGridSettings = Object.assign({
    antdProps: {
      dataSource,
      initRowFieldConfigs: (record: any) => {
        return initRowFieldConfigs();
      },
      pagination: false
    }
  }, gridSettings);

  useEffect(() => {
    /**
     * 起止都必填，选择年月；默认选中活动年的1月~当前年月（当前年月若小于活动年则为活动年1月，若大于活动年则为活动年12月）
     * 单选必填，可选为 第1层(大区)/第2层(省办)/第3层(市办)，对应3种查看客户权限，有权限才显示对应选项，默认优先选中最小级，即有大区权限则默认选中第1层
     */
    (async () => {
      if (formSa) {
        const fieldValues: any = {};
        if (boLevelOpts && boLevelOpts.length > 0) {
          fieldValues.boLevel = boLevelOpts[0];
        }
        const startYm = Number(moment(formSa.activityStartDate).format("YYYY")) * 100 + 1;
        const now = moment();
        let endYm = Number(now.format("YYYYMM"));
        if (formSa?.activityStartDate) {
          const activityYear = Number(moment(formSa.activityStartDate).format("YYYY"));
          const currentYear = Number(now.format("YYYY"));
          if (currentYear < activityYear) {
            endYm = activityYear * 100 + 1;
          } else if (currentYear > activityYear) {
            endYm = activityYear * 100 + 12;
          }
        }
        fieldValues.ym = [moment(startYm, "YYYYMM"), moment(endYm, "YYYYMM")];
        setFilterDefValues(fieldValues);
        doSearch(fieldValues);
      }
    })()
  }, []);

  const fetchData = (params: TableParams) => {
    // setLoading(true);
    const param: any = {
      formSaId: formSa?.formSaId,
    };
    if (params.filterOptions) {
      const {ym, boLevel, custSysAddCodes, brandIds} = params.filterOptions;
      if (ym && ym.length == 2) {
        param.startYm = moment(ym[0]).format('YYYYMM');
        param.endYm = moment(ym[1]).format('YYYYMM');
      }
      if (!wdiCore.String.IsNullOrEmpty(boLevel)) {
        param.boLevel = WdiSelectFormat.getvalue(boLevel);
      }
      if (!wdiCore.String.IsNullOrEmpty(custSysAddCodes)) {
        param.custSysAddCodes = WdiSelectFormat.getvalue(custSysAddCodes);
      }
      if (!wdiCore.String.IsNullOrEmpty(brandIds)) {
        param.brandIds = WdiSelectFormat.getvalue(brandIds);
      }
    }
    FormSa.searchRevenueExpenseSoldTo(param).then(responseResult => {
      const data = responseResult.data;
      if (data) {
        setDataSource(data);
        setTableParams({
          filterOptions: params.filterOptions
        });
        // setLoading(false);
      }
    });
  };
  const doSearch = (filterOptions: any) => {
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  };

  const buttonColumns: ButtonColumn[] = [
    {
      title: '导出',
      key: 'Export',
      doClick: () => {
        getSchemaColumns('SalesAndCostSoldTo', {}).then(columns => {
          const sheet = {
            data: dataSource,
            names: columns,
            sheetName: '销量与费用(客户)',
          };
          toExcel({
            name: '销量与费用(客户)_' + wdiCore.Date.ToString(new Date()) + '.xlsx',
            sheetProps: [sheet],
          });
        });
      },
    },
  ];
  const filterColumns: FilterColumn[] = [
    {
      title: '年月',
      dataIndex: 'ym',
      require: true,
      span: 4,
      render: () => {
        return <DatePicker.RangePicker picker="month" format={"YYYYMM"} style={{width: '100%'}}/>
      }
    },
    {
      title: '使用部门层级',
      dataIndex: 'boLevel',
      require: true,
      span: 4,
      render: () => {
        return <WdiSelect {...{
          label: "使用部门层级",
          options: boLevelOpts,
        }} />
      }
    },
    {
      title: '客户组补全',
      dataIndex: 'custSysAddCodes',
      span: 4,
      render: () => {
        return <WdiSelect {...{
          label: "客户组补全",
          showSearch: true,
          remote: true,
          dataLoading: async (params) => {
            const response = await CustSysAdd.ajaxSearch(Object.assign({
              soldToCodes: [...new Set(props.formSaDetails?.map(m => m.soldToCode))].join(),
              isActive: true,
            }, params));
            return response.data.map((item) => ({value: item.custSysAddCode, label: item.codeOrName,}));
          }
        }} />
      }
    },
    {
      title: '品牌',
      dataIndex: 'brandIds',
      span: 4,
      render: () => {
        return <WdiSelect {...{
          label: "品牌",
          showSearch: true,
          remote: true,
          dataLoading: async (params) => {
            const response = await Product.searchLevel1(Object.assign({
              productIds: [...new Set(props.formSaDetails?.map(m => m.brandId))].join(),
              isActive: true,
            }, params));
            return response.data.map((item) => ({value: item.productId, label: item.codeOrName,}));
          }
        }} />
      },
    },
  ];

  return (
    <>
      <ProCard
        type="inner"
        headStyle={{height: 40, backgroundColor: 'white', padding: '0'}}
        bodyStyle={{padding: '0px'}}
      >
        <GridFilterForm
          initialValues={filterDefValues}
          doSearch={doSearch}
          columns={filterColumns}
          buttonColumns={buttonColumns}
        />
        {isLoaded && (
          <DetailGrid {...pageGridSettings} />
        )}
      </ProCard>
    </>
  );
};

/**
 * 销量与费用(客户)
 */
export const SalesAndCostRegion: React.FC<SalesAndCostProps> = (props) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    filterOptions: null,
  });
  const [filterDefValues, setFilterDefValues] = useState<any>({});

  const {formSa, boLevelOpts} = props;

  useEffect(() => {
    Promise.all([
      GridApi.searchConfig({id: 'SalesAndCostRegion', allowModifyColumns: true}),
      GridApi.searchConfig({id: 'SalesAndCostRegionPd', allowModifyColumns: true}),
    ]).then((responseArr) => {
      const settings: any = Object.assign({}, formSa?.pageTypeId == Enum.PageType.PRICE_DISCOUNT_APPLICATION ? responseArr[1].data : responseArr[0].data, {
        actionsAlign: NsGrid.ActionsAlign.Left,
        // showAggr: true,
        showExpandedRow: true,
      });

      initFieldDisplayFormater(settings.formSchema.allFields);
      formatFormSchema(settings);

      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, []);

  const pageGridSettings = Object.assign({
    antdProps: {
      dataSource,
      initRowFieldConfigs: (record: any) => {
        return initRowFieldConfigs();
      },
      pagination: false
    }
  }, gridSettings);

  useEffect(() => {
    /**
     * 起止都必填，选择年月；默认选中活动年的1月~当前年月（当前年月若小于活动年则为活动年1月，若大于活动年则为活动年12月）
     * 单选必填，可选为 第1层(大区)/第2层(省办)/第3层(市办)，对应3种查看客户权限，有权限才显示对应选项，默认优先选中最小级，即有大区权限则默认选中第1层
     */
    (async () => {
      if (formSa) {
        const fieldValues: any = {};
        if (boLevelOpts && boLevelOpts.length > 0) {
          fieldValues.boLevel = boLevelOpts[0];
        }
        const startYm = Number(moment(formSa.activityStartDate).format("YYYY")) * 100 + 1;
        const now = moment();
        let endYm = Number(now.format("YYYYMM"));
        if (formSa?.activityStartDate) {
          const activityYear = Number(moment(formSa.activityStartDate).format("YYYY"));
          const currentYear = Number(now.format("YYYY"));
          if (currentYear < activityYear) {
            endYm = activityYear * 100 + 1;
          } else if (currentYear > activityYear) {
            endYm = activityYear * 100 + 12;
          }
        }
        fieldValues.ym = [moment(startYm, "YYYYMM"), moment(endYm, "YYYYMM")];
        setFilterDefValues(fieldValues);
        doSearch(fieldValues);
      }
    })()
  }, []);

  const fetchData = (params: TableParams) => {
    // setLoading(true);
    const param: any = {
      formSaId: formSa?.formSaId,
    };
    if (params.filterOptions) {
      const {ym, boLevel} = params.filterOptions;
      if (ym && ym.length == 2) {
        param.startYm = moment(ym[0]).format('YYYYMM');
        param.endYm = moment(ym[1]).format('YYYYMM');
      }
      if (!wdiCore.String.IsNullOrEmpty(boLevel)) {
        param.boLevel = WdiSelectFormat.getvalue(boLevel);
      }
    }
    FormSa.searchRevenueExpenseRegion(param).then(responseResult => {
      const data = responseResult.data;
      if (data) {
        setDataSource(data);
        setTableParams({
          filterOptions: params.filterOptions
        });
        // setLoading(false);
      }
    });
  };
  const doSearch = (filterOptions: any) => {
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  };

  const buttonColumns: ButtonColumn[] = [
    {
      title: '导出',
      key: 'Export',
      doClick: () => {
        getSchemaColumns('SalesAndCostRegion', {}).then(columns => {
          const sheet = {
            data: dataSource,
            names: columns,
            sheetName: '销量与费用(客户)',
          };
          toExcel({
            name: '销量与费用(客户)_' + wdiCore.Date.ToString(new Date()) + '.xlsx',
            sheetProps: [sheet],
          });
        });
      },
    },
  ];
  const filterColumns: FilterColumn[] = [
    {
      title: '年月',
      dataIndex: 'ym',
      require: true,
      span: 4,
      render: () => {
        return <DatePicker.RangePicker picker="month" format={"YYYYMM"} style={{width: '100%'}}/>
      }
    },
    {
      title: '使用部门层级',
      dataIndex: 'boLevel',
      require: true,
      span: 4,
      render: () => {
        return <WdiSelect {...{
          label: "使用部门层级",
          options: boLevelOpts,
        }} />
      }
    },
  ];

  return (
    <>
      <ProCard
        type="inner"
        headStyle={{height: 40, backgroundColor: 'white', padding: '0'}}
        bodyStyle={{padding: '0px'}}
      >
        <GridFilterForm
          initialValues={filterDefValues}
          doSearch={doSearch}
          columns={filterColumns}
          buttonColumns={buttonColumns}
        />
        {isLoaded && (
          <DetailGrid {...pageGridSettings} />
        )}
      </ProCard>
    </>
  );
};

